// CAP → comune lookup and Italian list formatting for the Sicilia (SR/CT)
// free-shipping zone. Built on top of utils/shipping.js (read-only, not modified)
// so texts stay in sync with settings.commerce.shipping_zones[].caps — adding or
// removing a comune's CAP in Admin > Aspetto > Zone spedizione updates this map too.
import { zoneCapsForProvince } from "./shipping";

export const CAP_COMUNE_MAP = {
  "96100": "Siracusa",
  "95100": "Catania", "95121": "Catania", "95122": "Catania", "95123": "Catania",
  "95124": "Catania", "95125": "Catania", "95126": "Catania", "95127": "Catania",
  "95128": "Catania", "95129": "Catania", "95130": "Catania", "95131": "Catania",
  "95031": "Adrano",
  "95032": "Belpasso",
  "95033": "Biancavilla",
  "95040": "Licodia Eubea",
  "95047": "Paternò",
};

// Admin-added CAPs don't need a code change to get a name in the checkout texts:
// each shipping zone has an admin-editable "comuni_names" field (Admin > Aspetto >
// Zone spedizione), formatted as "CAP:Nome, CAP:Nome, ...". This is only a fallback —
// the hardcoded map above still wins for CAPs it already knows, so existing texts
// never change just because settings loaded slower than usual.
const zoneComuneNamesFromSettings = (settings) => {
  const zones = settings?.commerce?.shipping_zones;
  if (!Array.isArray(zones)) return {};
  const map = {};
  zones.forEach((zone) => {
    String(zone?.comuni_names || "").split(",").forEach((pair) => {
      const [cap, name] = pair.split(":").map((s) => (s || "").trim());
      if (cap && name) map[cap] = name;
    });
  });
  return map;
};

export const comuneNameForCap = (cap, settings) => {
  const c = String(cap || "").trim();
  if (!c) return "";
  return CAP_COMUNE_MAP[c] || zoneComuneNamesFromSettings(settings)[c] || "";
};

// Joins a list the Italian way: "A", "A e B", "A, B e C"
const joinItalian = (names) => {
  if (!names.length) return "";
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(", ")} e ${names[names.length - 1]}`;
};

// Unique, ordered list of comune names covered by the given CAP list.
export const comuniForCaps = (caps, settings) => {
  const names = [];
  (caps || []).forEach((cap) => {
    const name = comuneNameForCap(cap, settings);
    if (name && !names.includes(name)) names.push(name);
  });
  return names;
};

export const comuniListText = (caps, settings) => joinItalian(comuniForCaps(caps, settings));

// Comuni served by the free-shipping zone matching this province, e.g. for "CT" →
// "Catania, Adrano, Belpasso, Biancavilla, Licodia Eubea e Paternò".
export const comuniTextForProvince = (province, settings) =>
  comuniListText(zoneCapsForProvince(province, settings), settings);

export const comuniForProvince = (province, settings) =>
  comuniForCaps(zoneCapsForProvince(province, settings), settings);

// All CAPs covered by a free (rate === 0) shipping zone, across every zone —
// used by the checkout's "Mappa comuni" to show every comune that ships free,
// not just the ones matching whatever province the customer already typed.
export const allFreeShippingCaps = (settings) => {
  const zones = settings?.commerce?.shipping_zones;
  if (!Array.isArray(zones)) return [];
  const caps = [];
  zones.forEach((zone) => {
    if (Number(zone?.rate) !== 0) return;
    String(zone?.caps || "").split(",").map((c) => c.trim()).filter(Boolean).forEach((cap) => {
      if (!caps.includes(cap)) caps.push(cap);
    });
  });
  return caps;
};

export const allFreeShippingComuni = (settings) => comuniForCaps(allFreeShippingCaps(settings), settings);

// Reverse lookup for the map's "Seleziona" action: the first known CAP for a
// given comune name (any one of its CAPs works equally for the shipping calc).
export const firstCapForComune = (comuneName, settings) => {
  const target = String(comuneName || "").trim().toLowerCase();
  if (!target) return "";
  const combined = { ...zoneComuneNamesFromSettings(settings), ...CAP_COMUNE_MAP };
  const found = Object.entries(combined).find(([, name]) => name.toLowerCase() === target);
  return found ? found[0] : "";
};

// Sicilian CAP ranges map 1:1 to province: 96xxx → Siracusa, 95xxx → Catania.
export const provinceForCap = (cap) => {
  const c = String(cap || "").trim();
  if (c.startsWith("96")) return "SR";
  if (c.startsWith("95")) return "CT";
  return "";
};
